/**
 * PostHog Analytics Service for Wealfi
 *
 * Centralized analytics tracking for user events, engagement, and KPIs.
 * Uses posthog-js on web (autocaptures $pageview, $pageleave, UTMs, web vitals)
 * and posthog-react-native on mobile.
 */

import { Platform } from 'react-native';
import logger from './logger';

// ==========================================
// TYPES & INTERFACES
// ==========================================

/**
 * All trackable events in the application
 * Keep in sync with backend event names for consistency
 */
export enum AnalyticsEvent {
  // Landing Page
  LANDING_PAGE_VIEWED = 'landing_page_viewed',
  LANDING_SCROLL_DEPTH = 'landing_scroll_depth',
  LANDING_CTA_CLICKED = 'landing_cta_clicked',
  LANDING_DEMO_MODAL_OPENED = 'landing_demo_modal_opened',
  LANDING_DEMO_TAB_CHANGED = 'landing_demo_tab_changed',
  LANDING_WHATSAPP_CLICKED = 'landing_whatsapp_clicked',
  WHATSAPP_FAB_CLICKED = 'whatsapp_fab_clicked',
  LANDING_NAV_CLICKED = 'landing_nav_clicked',
  LANDING_TIME_SPENT = 'landing_time_spent',

  // Activation & Onboarding
  CHAT_OPENED = 'message_opened',
  FIRST_MESSAGE_SENT = 'first_message_sent',
  PROFILE_COMPLETED = 'profile_completed',
  FINANCIAL_PROFILE_COMPLETED = 'financial_profile_completed',
  USER_TRIGGERED_SNAPSHOT = 'user_triggered_snapshot',
  DIAGNOSTIC_VIEWED = 'diagnostic_viewed',

  // AI Value (Core Differentiation)
  // Use exact same event names as backend for consistency
  FINANCIAL_GOAL_DETECTED_BY_AI = 'financial_goal_detected_by_ai',
  FINANCIAL_GOAL_CONFIRMED = 'financial_goal_confirmed',
  FINANCIAL_GOALS_VIEWED = 'financial_goals_viewed',
  AI_INSIGHT_GENERATED = 'ai_insight_generated',
  AI_INSIGHT_LIKED = 'ai_insight_liked',
  AI_INSIGHT_DISMISSED = 'ai_insight_dismissed',

  // Education
  EDUCATIONAL_CONTENT_SUGGESTED = 'educational_content_suggested',
  EDUCATIONAL_CONTENT_CLICKED = 'educational_content_clicked',
  EDUCATIONAL_CONTENT_COMPLETED = 'educational_content_completed',

  // Retention
  RETURNED_AFTER_24H = 'returned_after_24h',
  RETURNED_AFTER_7D = 'returned_after_7d',
  AI_PROACTIVE_MESSAGE_OPENED = 'ai_proactive_message_opened',
  AI_PROACTIVE_MESSAGE_IGNORED = 'ai_proactive_message_ignored',

  // Business
  PRICING_VIEWED = 'pricing_viewed',
  UPGRADE_CLICKED = 'upgrade_clicked',
  PLAN_SELECTED = 'plan_selected',
  PAYMENT_COMPLETED = 'payment_completed',

  // Tools Hub
  TOOLS_HUB_VIEWED = 'tools_hub_viewed',
  TOOLS_HUB_TOOL_CLICKED = 'tools_hub_tool_clicked',

  // Compound Interest Calculator
  COMPOUND_CALCULATOR_VIEWED = 'compound_calculator_viewed',
  COMPOUND_CALCULATOR_INPUT_CHANGED = 'compound_calculator_input_changed',
  COMPOUND_CALCULATOR_SCENARIO_SELECTED = 'compound_calculator_scenario_selected',
  COMPOUND_CALCULATOR_CTA_CLICKED = 'compound_calculator_cta_clicked',
}

/**
 * User properties tracked across the user lifecycle
 */
export interface UserProperties {
  user_id: string | number;
  email?: string;
  plan: 'free' | 'premium';
  financial_level?: 'beginner' | 'intermediate' | 'advanced';
  country?: string;
}

/**
 * Global properties attached to all events automatically
 */
export interface GlobalProperties {
  platform: 'web' | 'mobile';
  current_page: string;
  session_duration: number;
  user_plan: 'free' | 'premium';
}

/**
 * Custom event properties (extensible)
 */
export type EventProperties = Record<string, string | number | boolean | null | undefined>;

// ==========================================
// CONFIGURATION
// ==========================================

/**
 * PostHog configuration from environment variables
 */
const config = {
  apiKey: process.env.EXPO_PUBLIC_POSTHOG_API_KEY || '',
  host: process.env.EXPO_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
  enabled: process.env.EXPO_PUBLIC_ANALYTICS_ENABLED === 'true' || !__DEV__,
};

const isWeb = Platform.OS === 'web';

// ==========================================
// STATE
// ==========================================

// Unified client interface - either posthog-js or posthog-react-native
let posthogClient: any = null;
let isInitialized = false;
let currentPage = 'app_root';
let currentUserPlan: 'free' | 'premium' = 'free';
let sessionStartTime = Date.now();

// Debouncing map: tracks last event timestamp
const eventDebounceMap = new Map<string, number>();
const DEBOUNCE_WINDOW_MS = 2000; // 2 seconds
const MAX_MAP_SIZE = 100;

// Session-based deduplication for once-per-session events
const sessionEventSet = new Set<string>();

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Detect platform: web or mobile
 */
function getPlatform(): 'web' | 'mobile' {
  return isWeb ? 'web' : 'mobile';
}

/**
 * Get current session duration in seconds
 */
function getSessionDuration(): number {
  return Math.floor((Date.now() - sessionStartTime) / 1000);
}

/**
 * Build global properties to attach to all events
 */
function getGlobalProperties(): GlobalProperties {
  return {
    platform: getPlatform(),
    current_page: currentPage,
    session_duration: getSessionDuration(),
    user_plan: currentUserPlan,
  };
}

/**
 * Check if event should be tracked (debouncing logic)
 * Prevents same event from firing within DEBOUNCE_WINDOW_MS
 */
function shouldTrackEvent(eventName: string): boolean {
  const now = Date.now();
  const lastTracked = eventDebounceMap.get(eventName);

  // Check if within debounce window
  if (lastTracked && (now - lastTracked) < DEBOUNCE_WINDOW_MS) {
    return false; // Skip duplicate
  }

  // Update timestamp
  eventDebounceMap.set(eventName, now);

  // Cleanup old entries if map gets too large
  if (eventDebounceMap.size > MAX_MAP_SIZE) {
    const entries = Array.from(eventDebounceMap.entries());
    entries.sort((a, b) => a[1] - b[1]);

    // Keep only the 50 most recent
    const toKeep = entries.slice(-50);
    eventDebounceMap.clear();
    toKeep.forEach(([key, value]) => eventDebounceMap.set(key, value));
  }

  return true;
}

// ==========================================
// PUBLIC API
// ==========================================

/**
 * Analytics Service
 * Singleton service for PostHog event tracking
 */
export const analytics = {
  /**
   * Initialize PostHog client
   * Uses posthog-js on web, posthog-react-native on mobile
   *
   * @returns {boolean} True if initialized successfully
   */
  initialize(): boolean {
    if (isInitialized) {
      if (__DEV__) {
        logger.log('[Analytics] PostHog already initialized');
      }
      return true;
    }

    // Debug: Log configuration values
    if (__DEV__) {
      logger.log('[Analytics] Configuration:', {
        apiKey: config.apiKey ? `${config.apiKey.substring(0, 10)}...` : 'missing',
        host: config.host,
        enabled: config.enabled,
        platform: isWeb ? 'web (posthog-js)' : 'mobile (posthog-react-native)',
      });
    }

    if (!config.apiKey || !config.enabled) {
      if (__DEV__) {
        logger.log('[Analytics] PostHog disabled - Reason:', {
          noApiKey: !config.apiKey,
          notEnabled: !config.enabled,
        });
      }
      return false;
    }

    try {
      if (isWeb) {
        // Use posthog-js on web for full web analytics support
        const posthogJs = require('posthog-js').default;
        posthogJs.init(config.apiKey, {
          api_host: config.host,
          //api_host: '/ingest',
          //ui_host: config.host,
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: true,
          scroll_depth: true,
          enable_heatmaps: true,
        });
        posthogClient = posthogJs;
      } else {
        // Use posthog-react-native on mobile
        const PostHogRN = require('posthog-react-native').default;
        posthogClient = new PostHogRN(config.apiKey, {
          host: config.host,
          captureScreens: true,
          captureDeepLinks: true,
        });
      }

      isInitialized = true;

      if (__DEV__) {
        logger.log(`[Analytics] PostHog initialized successfully (${isWeb ? 'posthog-js' : 'posthog-react-native'})`);
      }

      return true;
    } catch (error) {
      logger.error('[Analytics] Failed to initialize PostHog:', error);
      return false;
    }
  },

  /**
   * Identify a user with their properties
   * Call when user logs in or signs up
   *
   * @param userId - Unique user identifier
   * @param properties - User properties
   */
  identifyUser(userId: string | number, properties: UserProperties): void {
    if (!posthogClient || !isInitialized) {
      if (__DEV__) {
        logger.log('[Analytics] Cannot identify user - not initialized');
      }
      return;
    }

    try {
      currentUserPlan = properties.plan;

      posthogClient.identify(String(userId), properties);

      if (__DEV__) {
        logger.log('[Analytics] User identified:', userId, properties);
      }
    } catch (error) {
      logger.error('[Analytics] Failed to identify user:', error);
    }
  },

  /**
   * Track an event with optional properties
   * Automatically adds global properties
   *
   * @param eventName - Event name from AnalyticsEvent enum
   * @param properties - Optional event-specific properties
   */
  trackEvent(eventName: AnalyticsEvent, properties?: EventProperties): void {
    if (!posthogClient || !isInitialized) {
      if (__DEV__) {
        logger.log('[Analytics] Event skipped (not initialized):', eventName);
      }
      return;
    }

    if (!shouldTrackEvent(eventName)) {
      if (__DEV__) {
        logger.log('[Analytics] Event skipped (debounced):', eventName);
      }
      return;
    }

    try {
      const enrichedProperties = {
        ...getGlobalProperties(),
        ...properties,
      };

      posthogClient.capture(eventName, enrichedProperties);

      if (__DEV__) {
        logger.log('[Analytics] Event tracked:', eventName, enrichedProperties);
      }
    } catch (error) {
      // NEVER crash the app for analytics
      logger.error('[Analytics] Failed to track event:', error);
    }
  },

  /**
   * Track an event only once per session
   * Useful for retention events like returned_after_24h
   *
   * @param eventName - Event name from AnalyticsEvent enum
   * @param properties - Optional event-specific properties
   */
  trackOncePerSession(eventName: AnalyticsEvent, properties?: EventProperties): void {
    if (sessionEventSet.has(eventName)) {
      if (__DEV__) {
        logger.log('[Analytics] Event skipped (already tracked this session):', eventName);
      }
      return;
    }

    sessionEventSet.add(eventName);
    this.trackEvent(eventName, properties);
  },

  /**
   * Reset user identity
   * Call when user logs out
   */
  resetUser(): void {
    if (!posthogClient || !isInitialized) {
      return;
    }

    try {
      posthogClient.reset();
      currentUserPlan = 'free';
      sessionEventSet.clear();

      if (__DEV__) {
        logger.log('[Analytics] User reset');
      }
    } catch (error) {
      logger.error('[Analytics] Failed to reset user:', error);
    }
  },

  /**
   * Update global properties
   * Call when navigation changes or user plan updates
   *
   * @param properties - Partial global properties to update
   */
  updateGlobalProperties(properties: Partial<GlobalProperties>): void {
    if (properties.current_page) {
      currentPage = properties.current_page;
    }
    if (properties.user_plan) {
      currentUserPlan = properties.user_plan;
    }
  },

  /**
   * Update user properties
   * Call when user profile changes (e.g., financial level update)
   *
   * @param properties - Partial user properties to update
   */
  updateUserProperties(properties: Partial<UserProperties>): void {
    if (!posthogClient || !isInitialized) {
      return;
    }

    try {
      if (isWeb) {
        // posthog-js uses setPersonProperties
        posthogClient.setPersonProperties(properties);
      } else {
        // posthog-react-native uses set (register is for super properties)
        posthogClient.set(properties);
      }

      if (properties.plan) {
        currentUserPlan = properties.plan;
      }

      if (__DEV__) {
        logger.log('[Analytics] User properties updated:', properties);
      }
    } catch (error) {
      logger.error('[Analytics] Failed to update user properties:', error);
    }
  },

  /**
   * Reset session
   * Call when app is reopened or comes to foreground
   */
  resetSession(): void {
    sessionStartTime = Date.now();
    sessionEventSet.clear();

    if (__DEV__) {
      logger.log('[Analytics] Session reset');
    }
  },

  /**
   * Check if analytics is initialized
   * @returns {boolean} True if initialized
   */
  isReady(): boolean {
    return isInitialized && posthogClient !== null;
  },
};

/**
 * Helper function to calculate financial level from profile
 * Used by frontend to determine user's financial sophistication
 *
 * @param profile - Financial profile data
 * @returns Financial level classification
 */
export function calculateFinancialLevel(profile: any): 'beginner' | 'intermediate' | 'advanced' {
  const hasSavings = (profile?.current_savings || 0) > 0;
  const hasGoals = (profile?.financial_goals || []).length > 0;
  const hasInvestmentProfile = !!profile?.risk_tolerance;
  const hasEmergencyFund = profile?.has_emergency_fund === true;

  if (hasSavings && hasGoals && hasInvestmentProfile && hasEmergencyFund) {
    return 'advanced';
  } else if ((hasSavings && hasGoals) || hasInvestmentProfile) {
    return 'intermediate';
  }
  return 'beginner';
}

// ==========================================
// DEVELOPMENT/TESTING: Expose to window
// ==========================================

/**
 * Expose analytics to window object for testing in browser console
 * Only in development or when explicitly enabled
 */
if (typeof window !== 'undefined') {
  (window as any).__wealfiAnalytics = {
    analytics,
    AnalyticsEvent,
    calculateFinancialLevel,
  };
}
